import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "../../../lib/api";

/**
 * Clients tab — lists customers with notes + per-customer profile modal.
 *
 * Extracted from BusinessDashboardShell.jsx (iter28 refactor step 2).
 * Bundles `CustomerProfileModal` and `ProfileStat` because they are private
 * to this view (only used here). No logic, UX or API change — only relocated.
 *
 * Capability: gated by `customers:view` at the shell level. Backend enforces
 * `customers:view` / `customers:update` on each endpoint, independently.
 */
export default function Clients({ businessId }) {
  const [items, setItems] = useState([]);
  const [dormant, setDormant] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [profileCid, setProfileCid] = useState(null);
  const [showDormant, setShowDormant] = useState(false);

  async function load() {
    const [a, b] = await Promise.all([
      api.get(`/businesses/${businessId}/customers`),
      api.get(`/businesses/${businessId}/customers/dormant`),
    ]);
    setItems(a.data);
    setDormant(b.data || []);
  }
  useEffect(() => { load(); }, [businessId]);

  async function saveNote(cid) {
    await api.put(`/businesses/${businessId}/customers/${cid}/note`, { text: noteText });
    setEditingNote(null);
    load();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="font-display text-xl font-medium">Clienti ({items.length})</h3>
        {dormant.length > 0 && (
          <button
            onClick={() => setShowDormant((v) => !v)}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-medium bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 dark:bg-amber-950/40 dark:border-amber-800/50 dark:text-amber-300"
            data-testid="dormant-toggle"
          >
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            {dormant.length} da riattivare
          </button>
        )}
      </div>

      {showDormant && dormant.length > 0 && (
        <div className="card-soft p-5 mb-5 bg-amber-50/30 border border-amber-100 dark:bg-amber-950/40 dark:border-amber-900/50" data-testid="dormant-panel">
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-amber-800 mb-1 dark:text-amber-300">Clienti inattivi</div>
          <p className="text-sm text-zinc-700 mb-4 dark:text-zinc-300">
            Questi clienti hanno superato il loro ciclo medio di visita. Contattali per invitarli a tornare.
          </p>
          <div className="space-y-2">
            {dormant.map((d) => {
              const mailto = d.customer_email
                ? `mailto:${d.customer_email}?subject=${encodeURIComponent("Ti aspettiamo di nuovo")}&body=${encodeURIComponent(`Ciao ${d.customer_name},\n\nsono passati ${d.days_since} giorni dalla tua ultima visita. Ti aspettiamo presto!\n`)}`
                : null;
              return (
                <div key={d.customer_id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-amber-100 dark:bg-zinc-900 dark:border-amber-900/50" data-testid={`dormant-${d.customer_id}`}>
                  <button onClick={() => setProfileCid(d.customer_id)} className="h-10 w-10 rounded-xl bg-amber-600 text-white flex items-center justify-center font-display text-sm">
                    {d.customer_name?.[0]?.toUpperCase() || "?"}
                  </button>
                  <div className="flex-1 min-w-0">
                    <button onClick={() => setProfileCid(d.customer_id)} className="font-medium text-left hover:underline block truncate">{d.customer_name}</button>
                    <div className="text-[11px] text-zinc-500 truncate dark:text-zinc-400">
                      {d.days_since} giorni · {d.visits} visite · {d.reason}
                    </div>
                  </div>
                  {mailto ? (
                    <a href={mailto} className="text-xs text-blue-600 hover:underline whitespace-nowrap">Scrivi email</a>
                  ) : (
                    <span className="text-[11px] text-zinc-400 dark:text-zinc-500">Nessuna email</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {items.length === 0 && <div className="text-sm text-zinc-500 dark:text-zinc-400">Nessun cliente ha ancora prenotato.</div>}
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.customer_id} className="card-soft p-5" data-testid={`client-${c.customer_id}`}>
            <div className="flex items-start gap-4 flex-wrap">
              <button
                onClick={() => setProfileCid(c.customer_id)}
                className="h-12 w-12 rounded-2xl bg-zinc-900 text-white flex items-center justify-center font-display hover:ring-2 hover:ring-blue-500 transition"
                data-testid={`client-open-${c.customer_id}`}
                aria-label="Apri profilo cliente"
              >
                {c.customer_name?.[0]?.toUpperCase() || "?"}
              </button>
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => setProfileCid(c.customer_id)}
                  className="font-medium text-left hover:underline"
                >
                  {c.customer_name}
                </button>
                <div className="text-xs text-zinc-500 flex gap-4 mt-1 flex-wrap dark:text-zinc-400">
                  <span>{c.count} {c.count === 1 ? "visita" : "visite"}</span>
                  <span>Ultima: {new Date(c.last_visit).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  <span>Totale: €{c.total_spent?.toFixed(2) || 0}</span>
                </div>
              </div>
              {c.count >= 5 && <span className="text-[10px] font-semibold tracking-widest uppercase text-amber-600 bg-amber-50 px-2 py-1 rounded-full dark:bg-amber-950/40">VIP</span>}
              {dormant.some((d) => d.customer_id === c.customer_id) && (
                <span className="text-[10px] font-semibold tracking-widest uppercase text-amber-800 bg-amber-100 px-2 py-1 rounded-full dark:text-amber-300 dark:bg-amber-950/40" data-testid={`dormant-badge-${c.customer_id}`}>Da riattivare</span>
              )}
            </div>
            <div className="mt-4">
              {editingNote === c.customer_id ? (
                <div className="space-y-2">
                  <textarea rows={3} className="input-soft h-auto py-3" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Note operative: preferenze, avvertenze comportamentali, indicazioni di servizio…" data-testid={`note-textarea-${c.customer_id}`} />
                  <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                    ⚠️ Non inserire dati sanitari (diagnosi, terapie, allergie cliniche). La piattaforma non è progettata per il trattamento di categorie particolari ex art. 9 GDPR — usa una cartella clinica dedicata.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => saveNote(c.customer_id)} className="btn-accent h-10 text-sm" data-testid={`note-save-${c.customer_id}`}>Salva nota</button>
                    <button onClick={() => setEditingNote(null)} className="btn-secondary h-10 text-sm">Annulla</button>
                  </div>
                </div>
              ) : c.note ? (
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 flex items-start gap-2 dark:bg-amber-950/40 dark:border-amber-900/50">
                  <div className="flex-1 text-sm text-amber-900 whitespace-pre-wrap dark:text-amber-200">{c.note}</div>
                  <button onClick={() => { setEditingNote(c.customer_id); setNoteText(c.note); }} className="text-xs text-amber-700 hover:text-amber-900 dark:text-amber-300" data-testid={`note-edit-${c.customer_id}`}>Modifica</button>
                </div>
              ) : (
                <button onClick={() => { setEditingNote(c.customer_id); setNoteText(""); }} className="text-xs text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100" data-testid={`note-add-${c.customer_id}`}>
                  + Aggiungi nota interna
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {profileCid && (
        <CustomerProfileModal
          businessId={businessId}
          customerId={profileCid}
          onClose={() => setProfileCid(null)}
          onNoteSaved={load}
        />
      )}
    </div>
  );
}


function CustomerProfileModal({ businessId, customerId, onClose, onNoteSaved }) {
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${businessId}/customers/${customerId}/profile`).then((r) => setP(r.data));
  }, [businessId, customerId]);

  async function saveNote() {
    setSaving(true);
    try {
      await api.put(`/businesses/${businessId}/customers/${customerId}/note`, { text: noteDraft });
      setP({ ...p, note: noteDraft });
      setEditing(false);
      onNoteSaved && onNoteSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="customer-profile-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto card-soft dark:bg-zinc-900">
        {!p ? (
          <div className="p-8 text-sm text-zinc-500 dark:text-zinc-400">Caricamento profilo…</div>
        ) : (
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="h-14 w-14 rounded-2xl bg-zinc-900 text-white flex items-center justify-center font-display text-xl">
                {p.customer.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-2xl font-medium">{p.customer.name || "Cliente"}</div>
                <div className="text-xs text-zinc-500 flex gap-4 mt-1 flex-wrap dark:text-zinc-400">
                  {p.customer.email && <span>{p.customer.email}</span>}
                  {p.customer.phone && <span>{p.customer.phone}</span>}
                </div>
              </div>
              <button onClick={onClose} className="h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="profile-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <ProfileStat label="Visite" value={p.stats.confirmed} />
              <ProfileStat label="Spesa totale" value={`€${p.stats.total_spent.toFixed(2)}`} />
              <ProfileStat label="Frequenza" value={p.stats.avg_days_between_visits != null ? `${p.stats.avg_days_between_visits}g` : "—"} hint="gg medi tra visite" />
              <ProfileStat label="No-show" value={p.stats.no_show} bad={p.stats.no_show > 0} />
            </div>

            {/* Favorites */}
            {(p.favorites.services.length > 0 || p.favorites.staff) && (
              <div className="mb-6">
                <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-2 dark:text-zinc-400">Preferenze</h4>
                <div className="space-y-2">
                  {p.favorites.services.map((f, i) => (
                    <div key={f.service_id + i} className="flex items-center justify-between text-sm bg-zinc-50 rounded-xl px-3 py-2 dark:bg-zinc-800">
                      <span>{f.name}</span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">{f.count}×</span>
                    </div>
                  ))}
                  {p.favorites.staff && (
                    <div className="flex items-center justify-between text-sm bg-blue-50 rounded-xl px-3 py-2 dark:bg-blue-950/40">
                      <span>Operatore preferito: <strong>{p.favorites.staff.name}</strong></span>
                      <span className="text-xs text-blue-700 dark:text-blue-300">{p.favorites.staff.count}×</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Internal note */}
            <div className="mb-6">
              <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-2 dark:text-zinc-400">Nota interna</h4>
              {editing ? (
                <div className="space-y-2">
                  <textarea rows={3} className="input-soft h-auto py-3" value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Preferenze, avvertenze comportamentali, indicazioni di servizio…" data-testid="profile-note-textarea" />
                  <p className="text-[11px] text-zinc-500 dark:text-zinc-400">⚠️ Non inserire dati sanitari (diagnosi, terapie). Vedi <a href="/dpa" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">DPA art. 9</a>.</p>
                  <div className="flex gap-2">
                    <button onClick={saveNote} disabled={saving} className="btn-accent h-10 text-sm" data-testid="profile-note-save">{saving ? "Salva…" : "Salva"}</button>
                    <button onClick={() => setEditing(false)} className="btn-secondary h-10 text-sm">Annulla</button>
                  </div>
                </div>
              ) : p.note ? (
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 flex items-start gap-2 dark:bg-amber-950/40 dark:border-amber-900/50">
                  <div className="flex-1 text-sm text-amber-900 whitespace-pre-wrap dark:text-amber-200">{p.note}</div>
                  <button onClick={() => { setEditing(true); setNoteDraft(p.note); }} className="text-xs text-amber-700 hover:text-amber-900 dark:text-amber-300">Modifica</button>
                </div>
              ) : (
                <button onClick={() => { setEditing(true); setNoteDraft(""); }} className="text-xs text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100">+ Aggiungi nota interna</button>
              )}
            </div>

            {/* Recent bookings */}
            <div>
              <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-2 dark:text-zinc-400">Storico recente</h4>
              {p.recent_bookings.length === 0 ? (
                <div className="text-sm text-zinc-500 dark:text-zinc-400">Nessuna prenotazione ancora.</div>
              ) : (
                <div className="border border-zinc-100 rounded-2xl overflow-hidden dark:border-zinc-800">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-[11px] uppercase tracking-widest text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                      <tr>
                        <th className="text-left p-2 px-3">Data</th>
                        <th className="text-left p-2 px-3">Servizio</th>
                        <th className="text-left p-2 px-3">Operatore</th>
                        <th className="text-right p-2 px-3">Prezzo</th>
                        <th className="text-center p-2 px-3">Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.recent_bookings.map((b) => (
                        <tr key={b.id} className="border-t border-zinc-100 dark:border-zinc-800" data-testid={`profile-row-${b.id}`}>
                          <td className="p-2 px-3 whitespace-nowrap">{new Date(b.start_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" })}</td>
                          <td className="p-2 px-3 truncate max-w-[180px]">{b.service_name}</td>
                          <td className="p-2 px-3 text-zinc-600 text-xs dark:text-zinc-400">{b.staff_name || "—"}</td>
                          <td className="p-2 px-3 text-right">€{b.price?.toFixed ? b.price.toFixed(2) : b.price}</td>
                          <td className="p-2 px-3 text-center">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${b.status === "confirmed" || b.status === "completed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : b.status === "no_show" ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" : "bg-zinc-100 text-zinc-500"}`}>
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function ProfileStat({ label, value, hint, bad }) {
  return (
    <div className={`rounded-2xl p-3 ${bad ? "bg-amber-50 dark:bg-amber-950/40" : "bg-zinc-50"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">{label}</div>
      <div className={`font-display text-2xl mt-1 ${bad ? "text-amber-700 dark:text-amber-300" : "text-zinc-900"}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-400 mt-0.5 dark:text-zinc-500">{hint}</div>}
    </div>
  );
}
